import { useRef, useEffect } from 'react';
import { motion, useInView, useMotionValue, useTransform, animate } from 'framer-motion';
import { GlassCard } from '../ui/GlassCard';
import { CheckCircle, Wrench, Bot, Server, Cpu } from 'lucide-react';
import { cn } from '../../lib/utils';
import type { SystemMetrics, ToolSuccess, ActiveAgent } from '../../hooks/useMetricsData';

// ─── Animated Number ─────────────────────────────────────────────────

function AnimatedNumber({
  value,
  className,
}: {
  value: number;
  className?: string;
}) {
  const ref = useRef<HTMLSpanElement>(null);
  const motionVal = useMotionValue(0);
  const rounded = useTransform(motionVal, (v) => Math.round(v).toLocaleString());

  useEffect(() => {
    const controls = animate(motionVal, value, {
      duration: 0.8,
      ease: [0.25, 0.46, 0.45, 0.94],
    });
    const unsub = rounded.on('change', (v) => {
      if (ref.current) ref.current.textContent = v;
    });
    return () => {
      controls.stop();
      unsub();
    };
  }, [value, motionVal, rounded]);

  return <span ref={ref} className={className}>{Math.round(value).toLocaleString()}</span>;
}

// ─── Concurrent Calls ────────────────────────────────────────────────

function ConcurrentCalls({
  current,
  capacity,
}: {
  current: number;
  capacity: number;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true });
  const pct = Math.min((current / capacity) * 100, 100);

  let statusColor = 'bg-success';
  if (pct > 80) statusColor = 'bg-warning';
  if (pct > 95) statusColor = 'bg-error';

  return (
    <GlassCard
      variant="default"
      header={
        <div className="flex items-center gap-2">
          <Server className="w-4 h-4 text-vokentra" />
          <span className="text-sm font-semibold text-text-primary">Concurrent Calls</span>
        </div>
      }
    >
      <div ref={ref} className="space-y-4">
        {/* Big number */}
        <div className="flex items-end gap-2">
          <AnimatedNumber
            value={current}
            className="text-4xl font-bold text-text-primary tabular-nums"
          />
          <span className="text-sm text-text-muted mb-1">
            / {capacity.toLocaleString()} capacity
          </span>
        </div>

        {/* Progress bar */}
        <div className="space-y-1.5">
          <div className="flex items-center justify-between text-xs">
            <span className="text-text-muted">Utilization</span>
            <span className={cn('font-medium tabular-nums', pct > 80 ? 'text-warning' : 'text-success')}>
              {pct.toFixed(1)}%
            </span>
          </div>
          <div className="h-3 rounded-full bg-white/[0.06] overflow-hidden">
            <motion.div
              className={cn('h-full rounded-full', statusColor)}
              initial={{ width: 0 }}
              animate={isInView ? { width: `${pct}%` } : {}}
              transition={{ delay: 0.3, duration: 1, ease: [0.25, 0.46, 0.45, 0.94] }}
            />
          </div>
        </div>

        {/* Stats row */}
        <div className="grid grid-cols-2 gap-3 pt-2">
          <div className="p-2 rounded-lg bg-white/[0.02] text-center">
            <p className="text-lg font-semibold text-text-primary tabular-nums">
              {Math.round(capacity - current).toLocaleString()}
            </p>
            <p className="text-[10px] text-text-muted">Available</p>
          </div>
          <div className="p-2 rounded-lg bg-white/[0.02] text-center">
            <p className="text-lg font-semibold text-text-primary tabular-nums">
              {Math.round(current * 0.3).toLocaleString()}
            </p>
            <p className="text-[10px] text-text-muted">Peak (est.)</p>
          </div>
        </div>
      </div>
    </GlassCard>
  );
}

// ─── Success Rate Gauge ──────────────────────────────────────────────

function SuccessRateGauge({ rate }: { rate: number }) {
  const ref = useRef<SVGSVGElement>(null);
  const isInView = useInView(ref, { once: true });
  const radius = 55;
  const stroke = 10;
  const normalizedRadius = radius - stroke / 2;
  const circumference = normalizedRadius * 2 * Math.PI;
  const arc = circumference * 0.75; // 270-degree arc
  const fillPct = Math.max(0, Math.min(100, (rate - 95) / 5 * 100)); // Map 95-100% to 0-100%
  const strokeDashoffset = arc - (isInView ? fillPct / 100 : 0) * arc;

  const color = rate >= 99 ? '#00d084' : rate >= 95 ? '#ffa500' : '#ff3366';
  const labelColor = rate >= 99 ? 'text-success' : rate >= 95 ? 'text-warning' : 'text-error';

  return (
    <GlassCard
      variant="default"
      header={
        <div className="flex items-center gap-2">
          <CheckCircle className="w-4 h-4 text-success" />
          <span className="text-sm font-semibold text-text-primary">Success Rate</span>
        </div>
      }
    >
      <div className="flex flex-col items-center py-2">
        <svg
          ref={ref}
          width={radius * 2}
          height={radius * 1.6}
          viewBox={`0 0 ${radius * 2} ${radius * 1.6}`}
        >
          {/* Background arc */}
          <circle
            cx={radius}
            cy={radius * 1.1}
            r={normalizedRadius}
            fill="transparent"
            stroke="rgba(255,255,255,0.06)"
            strokeWidth={stroke}
            strokeDasharray={arc}
            strokeDashoffset={circumference - arc}
            strokeLinecap="round"
            transform={`rotate(135 ${radius} ${radius * 1.1})`}
          />
          {/* Progress arc */}
          <motion.circle
            cx={radius}
            cy={radius * 1.1}
            r={normalizedRadius}
            fill="transparent"
            stroke={color}
            strokeWidth={stroke}
            strokeDasharray={arc}
            strokeLinecap="round"
            transform={`rotate(135 ${radius} ${radius * 1.1})`}
            initial={{ strokeDashoffset: arc }}
            animate={isInView ? { strokeDashoffset: strokeDashoffset } : {}}
            transition={{ duration: 1.5, ease: [0.25, 0.46, 0.45, 0.94], delay: 0.3 }}
          />
          {/* Percentage */}
          <text
            x={radius}
            y={radius * 0.95}
            textAnchor="middle"
            fill="#ffffff"
            fontSize="20"
            fontWeight="bold"
            fontFamily="Inter, system-ui, sans-serif"
          >
            {rate.toFixed(1)}%
          </text>
          <text
            x={radius}
            y={radius * 1.25}
            textAnchor="middle"
            fill="rgba(255,255,255,0.4)"
            fontSize="9"
            fontFamily="Inter, system-ui, sans-serif"
          >
            Uptime
          </text>
        </svg>

        {/* Status */}
        <div className="mt-2 flex items-center gap-1.5">
          <span className={cn('w-2 h-2 rounded-full animate-pulse', labelColor.replace('text-', 'bg-'))} />
          <span className={cn('text-xs font-medium', labelColor)}>
            {rate >= 99 ? 'Excellent' : rate >= 97 ? 'Good' : rate >= 95 ? 'Fair' : 'Needs Attention'}
          </span>
        </div>
      </div>
    </GlassCard>
  );
}

// ─── Tool Call Success ───────────────────────────────────────────────

function ToolCallSuccess({ tools }: { tools: ToolSuccess[] }) {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true });

  const barColor = (rate: number): string => {
    if (rate >= 99.8) return '#00d084';
    if (rate >= 99) return '#00bfff';
    return '#ffa500';
  };

  return (
    <GlassCard
      variant="default"
      header={
        <div className="flex items-center gap-2">
          <Wrench className="w-4 h-4 text-info" />
          <span className="text-sm font-semibold text-text-primary">Tool Call Success</span>
        </div>
      }
    >
      <div ref={ref} className="space-y-3 py-1">
        {tools.map((tool, i) => {
          const displayRate = Math.max(0, Math.min(100, (tool.successRate - 98) * 50));
          return (
            <motion.div
              key={tool.tool}
              className="space-y-1"
              initial={{ opacity: 0, x: -15 }}
              animate={isInView ? { opacity: 1, x: 0 } : {}}
              transition={{ delay: i * 0.1, duration: 0.4 }}
            >
              <div className="flex items-center justify-between">
                <span className="text-xs text-text-secondary font-mono">{tool.tool}</span>
                <span className="text-xs font-semibold text-text-primary tabular-nums">
                  {tool.successRate.toFixed(1)}%
                </span>
              </div>
              <div className="h-2 rounded-full bg-white/[0.06] overflow-hidden">
                <motion.div
                  className="h-full rounded-full"
                  style={{ backgroundColor: barColor(tool.successRate) }}
                  initial={{ width: 0 }}
                  animate={isInView ? { width: `${displayRate}%` } : {}}
                  transition={{ delay: i * 0.1 + 0.2, duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] }}
                />
              </div>
            </motion.div>
          );
        })}
      </div>
    </GlassCard>
  );
}

// ─── Active Agents Grid ──────────────────────────────────────────────

function ActiveAgentsGrid({ agents }: { agents: ActiveAgent[] }) {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true });

  const statusConfig = {
    healthy: { dot: 'bg-success', pulse: 'animate-pulse-fast', shadow: 'shadow-glow-green' },
    warning: { dot: 'bg-warning', pulse: 'animate-pulse', shadow: 'shadow-[0_0_8px_rgba(255,165,0,0.6)]' },
    critical: { dot: 'bg-error', pulse: 'animate-pulse-fast', shadow: 'shadow-[0_0_8px_rgba(255,51,102,0.6)]' },
  };

  return (
    <GlassCard
      variant="default"
      header={
        <div className="flex items-center gap-2">
          <Bot className="w-4 h-4 text-warning" />
          <span className="text-sm font-semibold text-text-primary">Active Agents</span>
        </div>
      }
    >
      <div ref={ref} className="grid grid-cols-2 gap-3">
        {agents.map((agent, i) => {
          const cfg = statusConfig[agent.status];
          return (
            <motion.div
              key={agent.name}
              className="p-3 rounded-xl bg-white/[0.02] border border-white/[0.06] space-y-2"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={isInView ? { opacity: 1, scale: 1 } : {}}
              transition={{ delay: i * 0.1, duration: 0.4 }}
              whileHover={{ scale: 1.03, transition: { duration: 0.2 } }}
            >
              <div className="flex items-center justify-between">
                <span className="text-[10px] text-text-muted font-mono">
                  {agent.name.replace('Agent', '')}
                </span>
                <div className={cn('w-2 h-2 rounded-full', cfg.dot, cfg.pulse, cfg.shadow)} />
              </div>
              <p className="text-xl font-bold text-text-primary tabular-nums">
                {agent.count}
              </p>
              <p className="text-[10px] text-text-muted">active instances</p>
            </motion.div>
          );
        })}
      </div>

      {/* Total */}
      <div className="mt-3 p-2 rounded-lg bg-white/[0.02] text-center">
        <div className="flex items-center justify-center gap-2">
          <Cpu className="w-3 h-3 text-text-muted" />
          <span className="text-xs text-text-muted">Total Active:</span>
          <span className="text-sm font-bold text-text-primary tabular-nums">
            {agents.reduce((s, a) => s + a.count, 0)}
          </span>
        </div>
      </div>
    </GlassCard>
  );
}

// ─── Exported Component ──────────────────────────────────────────────

interface SystemPerformanceProps {
  system: SystemMetrics;
  className?: string;
}

export function SystemPerformance({ system, className }: SystemPerformanceProps) {
  return (
    <div className={cn('grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4', className)}>
      <ConcurrentCalls current={system.concurrentCalls} capacity={system.maxCapacity} />
      <SuccessRateGauge rate={system.successRate} />
      <ToolCallSuccess tools={system.toolSuccess} />
      <ActiveAgentsGrid agents={system.activeAgents} />
    </div>
  );
}

export default SystemPerformance;
